import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Search, RefreshCcw, Package, CheckCircle, Clock, Truck, Eye,
  DollarSign, FileText, Building2, Download, Loader2, XCircle,
  Check, X, Edit3, Plus, Minus, History, AlertTriangle, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RawMaterialOrders = ({ franchises = [] }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_orders: 0, pending_payments: 0, pending_deliveries: 0, total_revenue: 0 });
  const [filters, setFilters] = useState({
    franchise_id: 'all', payment_status: 'all', delivery_status: 'all',
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Action states
  const [actionLoading, setActionLoading] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showAlterModal, setShowAlterModal] = useState(false);
  const [alterItems, setAlterItems] = useState([]);
  const [alterAdditionalReq, setAlterAdditionalReq] = useState('');
  const [availableRawMaterials, setAvailableRawMaterials] = useState([]);
  const [showAddItemSelect, setShowAddItemSelect] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = { start_date: filters.start_date, end_date: filters.end_date };
      if (filters.franchise_id !== 'all') params.franchise_id = filters.franchise_id;
      if (filters.payment_status !== 'all') params.payment_status = filters.payment_status;
      if (filters.delivery_status !== 'all') params.delivery_status = filters.delivery_status;
      const response = await axios.get(`${API}/raw-material-orders`, { params, ...getAuthHeaders() });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/raw-material-orders/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => { fetchOrders(); fetchStats(); }, [fetchOrders, fetchStats]);

  const updateOrderStatus = async (orderId, paymentStatus, deliveryStatus) => {
    try {
      const response = await axios.put(`${API}/admin/raw-material-orders/status`, {
        order_id: orderId, payment_status: paymentStatus, delivery_status: deliveryStatus
      }, getAuthHeaders());
      if (response.data.success) {
        toast.success('Order status updated');
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, payment_status: paymentStatus || o.payment_status, delivery_status: deliveryStatus || o.delivery_status } : o));
        if (selectedOrder?.order_id === orderId) {
          setSelectedOrder(prev => ({ ...prev, payment_status: paymentStatus || prev.payment_status, delivery_status: deliveryStatus || prev.delivery_status }));
        }
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  // ==================== ADMIN ACTIONS ====================

  const handleAccept = async (orderId) => {
    setActionLoading('accept');
    try {
      const res = await axios.post(`${API}/admin/raw-material-orders/accept`, { order_id: orderId }, getAuthHeaders());
      if (res.data.success) {
        toast.success('Order accepted successfully');
        fetchOrders();
        // Refresh selected order
        const updated = await axios.get(`${API}/raw-material-orders/${orderId}`, getAuthHeaders());
        setSelectedOrder(updated.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept order');
    } finally {
      setActionLoading('');
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) { toast.error('Please enter a rejection reason'); return; }
    setActionLoading('reject');
    try {
      const res = await axios.post(`${API}/admin/raw-material-orders/reject`, {
        order_id: selectedOrder.order_id, reason: rejectReason.trim()
      }, getAuthHeaders());
      if (res.data.success) {
        toast.success('Order rejected');
        setShowRejectModal(false);
        setRejectReason('');
        fetchOrders();
        const updated = await axios.get(`${API}/raw-material-orders/${selectedOrder.order_id}`, getAuthHeaders());
        setSelectedOrder(updated.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject order');
    } finally {
      setActionLoading('');
    }
  };

  const openAlterModal = async (order) => {
    setAlterItems(order.ordered_items.map(it => ({ ...it })));
    setAlterAdditionalReq(order.additional_requirements || '');
    // Fetch raw materials for adding new items
    try {
      const res = await axios.get(`${API}/admin/raw-materials`, getAuthHeaders());
      setAvailableRawMaterials(res.data.materials || []);
    } catch { setAvailableRawMaterials([]); }
    setShowAlterModal(true);
  };

  const handleAlterSubmit = async () => {
    const validItems = alterItems.filter(it => it.quantity > 0);
    if (validItems.length === 0) { toast.error('Order must have at least one item'); return; }
    setActionLoading('alter');
    try {
      const res = await axios.post(`${API}/admin/raw-material-orders/alter`, {
        order_id: selectedOrder.order_id,
        altered_items: validItems.map(it => ({
          item_name: it.item_name, size_or_quantity: it.size_or_quantity || 'unit',
          quantity: it.quantity, price_per_unit: it.price_per_unit || 0,
          total_price: (it.quantity || 0) * (it.price_per_unit || 0)
        })),
        additional_requirements: alterAdditionalReq
      }, getAuthHeaders());
      if (res.data.success) {
        toast.success(`Order altered (${res.data.alter_count}/2 alterations used)`);
        setShowAlterModal(false);
        fetchOrders();
        const updated = await axios.get(`${API}/raw-material-orders/${selectedOrder.order_id}`, getAuthHeaders());
        setSelectedOrder(updated.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to alter order');
    } finally {
      setActionLoading('');
    }
  };

  const addNewItemToAlter = (material) => {
    const exists = alterItems.find(it => it.item_name === material.name);
    if (exists) { toast.info('Item already in order'); return; }
    setAlterItems(prev => [...prev, {
      item_name: material.name, size_or_quantity: material.unit || 'unit',
      quantity: 1, price_per_unit: material.price || material.default_price || 0,
      total_price: material.price || material.default_price || 0
    }]);
    setShowAddItemSelect(false);
  };

  const downloadPDF = (base64Data, orderId) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `Order_${orderId}.pdf`;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async (orderId, version = null) => {
    try {
      setDownloadingPdf(true);
      const url = version ? `${API}/raw-material-orders/${orderId}/pdf/${version}` : `${API}/raw-material-orders/${orderId}/pdf`;
      const response = await axios.get(url, getAuthHeaders());
      if (response.data.success && response.data.pdf_base64) {
        const suffix = version ? `_v${version}` : '';
        downloadPDF(response.data.pdf_base64, `${orderId}${suffix}`);
        toast.success('PDF downloaded');
      }
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getOrderStatusBadge = (status) => {
    const configs = {
      'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      'Accepted': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      'Rejected': { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      'Altered': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Edit3 }
    };
    const config = configs[status] || configs['Pending'];
    const Icon = config.icon;
    return (
      <span data-testid={`status-badge-${status?.toLowerCase()}`} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3" />{status || 'Pending'}
      </span>
    );
  };

  const getStatusBadge = (status, type) => {
    const configs = {
      payment: { 'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock }, 'Paid': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle }, 'Partially Paid': { bg: 'bg-orange-100', text: 'text-orange-700', icon: DollarSign } },
      delivery: { 'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock }, 'Delivered': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle }, 'In Transit': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Truck } }
    };
    const config = configs[type]?.[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock };
    const Icon = config.icon;
    return (<span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}><Icon className="h-3 w-3" />{status}</span>);
  };

  const filteredOrders = orders.filter(order =>
    order.order_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    order.franchise_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (ds) => {
    if (!ds) return 'N/A';
    try { return new Date(ds).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ds; }
  };

  // ==================== TIMELINE COMPONENT ====================
  const OrderTimeline = ({ logs }) => {
    if (!logs || logs.length === 0) return <p className="text-sm text-muted-foreground">No activity logs</p>;
    const actionColors = { CREATED: 'bg-blue-500', ACCEPTED: 'bg-green-500', REJECTED: 'bg-red-500', ALTERED: 'bg-amber-500' };
    const actionIcons = { CREATED: Package, ACCEPTED: CheckCircle, REJECTED: XCircle, ALTERED: Edit3 };
    return (
      <div className="space-y-3">
        {logs.map((log, idx) => {
          const Icon = actionIcons[log.action] || Clock;
          const color = actionColors[log.action] || 'bg-gray-500';
          return (
            <div key={idx} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
                {idx < logs.length - 1 && <div className="w-0.5 flex-1 bg-border mt-1" />}
              </div>
              <div className="flex-1 pb-3">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{log.action}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</span>
                </div>
                <p className="text-xs text-secondary">by {log.performed_by} ({log.performed_by_role})</p>
                {log.metadata?.reason && <p className="text-xs mt-1 p-2 bg-red-50 text-red-700 rounded">Reason: {log.metadata.reason}</p>}
                {log.metadata?.changes && log.metadata.changes.length > 0 && (
                  <div className="mt-1 p-2 bg-amber-50 rounded text-xs">
                    {log.metadata.changes.map((c, i) => <p key={i} className="text-amber-800">{c}</p>)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary">Raw Material Orders</h2>
          <p className="text-secondary mt-1">Track and manage franchise raw material orders</p>
        </div>
        <Button variant="outline" onClick={() => { fetchOrders(); fetchStats(); }} className="rounded-full">
          <RefreshCcw className="h-4 w-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-blue-500 rounded-lg"><FileText className="h-5 w-5 text-white" /></div><div><p className="text-sm text-blue-600">Total Orders</p><p className="text-2xl font-bold text-blue-700">{stats.total_orders}</p></div></div></CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-yellow-500 rounded-lg"><Clock className="h-5 w-5 text-white" /></div><div><p className="text-sm text-yellow-600">Pending Payments</p><p className="text-2xl font-bold text-yellow-700">{stats.pending_payments}</p></div></div></CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-orange-500 rounded-lg"><Truck className="h-5 w-5 text-white" /></div><div><p className="text-sm text-orange-600">Pending Delivery</p><p className="text-2xl font-bold text-orange-700">{stats.pending_deliveries}</p></div></div></CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-green-500 rounded-lg"><DollarSign className="h-5 w-5 text-white" /></div><div><p className="text-sm text-green-600">Total Revenue</p><p className="text-2xl font-bold text-green-700">Rs.{stats.total_revenue?.toLocaleString() || 0}</p></div></div></CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div><Label>Franchise</Label><Select value={filters.franchise_id} onValueChange={(v) => setFilters(p => ({ ...p, franchise_id: v }))}><SelectTrigger><SelectValue placeholder="All Franchises" /></SelectTrigger><SelectContent><SelectItem value="all">All Franchises</SelectItem>{franchises.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Payment Status</Label><Select value={filters.payment_status} onValueChange={(v) => setFilters(p => ({ ...p, payment_status: v }))}><SelectTrigger><SelectValue placeholder="All" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Partially Paid">Partially Paid</SelectItem><SelectItem value="Paid">Paid</SelectItem></SelectContent></Select></div>
            <div><Label>Delivery Status</Label><Select value={filters.delivery_status} onValueChange={(v) => setFilters(p => ({ ...p, delivery_status: v }))}><SelectTrigger><SelectValue placeholder="All" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Pending">Pending</SelectItem><SelectItem value="In Transit">In Transit</SelectItem><SelectItem value="Delivered">Delivered</SelectItem></SelectContent></Select></div>
            <div><Label>Start Date</Label><Input type="date" value={filters.start_date} onChange={(e) => setFilters(p => ({ ...p, start_date: e.target.value }))} /></div>
            <div><Label>End Date</Label><Input type="date" value={filters.end_date} onChange={(e) => setFilters(p => ({ ...p, end_date: e.target.value }))} /></div>
            <div><Label>Search</Label><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search orders..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div></div>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" /><p className="mt-2 text-secondary">Loading orders...</p></div>
      ) : filteredOrders.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">No orders found</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => (
            <Card key={order.order_id} className="hover:shadow-md transition-shadow" data-testid={`rm-order-card-${order.order_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-bold text-primary text-lg">{order.order_id}</span>
                      {getOrderStatusBadge(order.status)}
                      <span className="text-sm text-muted-foreground">{formatDate(order.timestamp)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-secondary">
                      <Building2 className="h-4 w-4" /><span>{order.franchise_name}</span><span className="text-muted-foreground">|</span><span>{order.location}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{order.ordered_items?.length || 0} items - Total: <span className="font-bold text-primary">Rs.{order.total_amount?.toLocaleString() || 0}</span></p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Payment:</span>{getStatusBadge(order.payment_status || 'Pending', 'payment')}</div>
                    <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Delivery:</span>{getStatusBadge(order.delivery_status || 'Pending', 'delivery')}</div>
                    <Button size="sm" variant="ghost" data-testid={`view-order-${order.order_id}`} onClick={() => { setSelectedOrder(order); setIsDetailsOpen(true); }}>
                      <Eye className="h-4 w-4 mr-1" />View
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ==================== ORDER DETAILS MODAL ==================== */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary">Order Details</DialogTitle>
            <DialogDescription>View and manage raw material order</DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-muted-foreground">Order ID</p><p className="font-bold text-primary">{selectedOrder.order_id}</p></div>
                <div><p className="text-sm text-muted-foreground">Status</p>{getOrderStatusBadge(selectedOrder.status)}</div>
                <div><p className="text-sm text-muted-foreground">Date & Time</p><p className="font-medium">{formatDate(selectedOrder.timestamp)}</p></div>
                <div><p className="text-sm text-muted-foreground">Franchise</p><p className="font-medium">{selectedOrder.franchise_name}</p></div>
                <div><p className="text-sm text-muted-foreground">Location</p><p className="font-medium">{selectedOrder.location}</p></div>
                <div><p className="text-sm text-muted-foreground">Total Amount</p><p className="font-bold text-primary text-xl">Rs.{selectedOrder.total_amount?.toLocaleString()}</p></div>
              </div>

              {/* Admin Action Buttons */}
              {selectedOrder.status !== 'Rejected' && (
                <div data-testid="admin-action-buttons" className="flex gap-3 p-4 bg-muted rounded-lg">
                  <Button data-testid="accept-order-btn" className="bg-green-600 hover:bg-green-700 text-white flex-1" disabled={actionLoading === 'accept' || selectedOrder.status === 'Accepted'} onClick={() => handleAccept(selectedOrder.order_id)}>
                    {actionLoading === 'accept' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    {selectedOrder.status === 'Accepted' ? 'Accepted' : 'Accept'}
                  </Button>
                  <Button data-testid="reject-order-btn" variant="destructive" className="flex-1" onClick={() => { setRejectReason(''); setShowRejectModal(true); }}>
                    <X className="h-4 w-4 mr-2" />Reject
                  </Button>
                  <Button data-testid="alter-order-btn" className="bg-amber-600 hover:bg-amber-700 text-white flex-1" disabled={(selectedOrder.alter_count || 0) >= 2} onClick={() => openAlterModal(selectedOrder)}>
                    <Edit3 className="h-4 w-4 mr-2" />Alter {selectedOrder.alter_count > 0 ? `(${selectedOrder.alter_count}/2)` : ''}
                  </Button>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedOrder.rejection_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-semibold text-red-700">Rejection Reason:</p>
                  <p className="text-sm text-red-600">{selectedOrder.rejection_reason}</p>
                </div>
              )}

              {/* Payment/Delivery Status */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <Label>Payment Status</Label>
                  <Select value={selectedOrder.payment_status || 'Pending'} onValueChange={(v) => updateOrderStatus(selectedOrder.order_id, v, null)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Partially Paid">Partially Paid</SelectItem><SelectItem value="Paid">Paid</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Delivery Status</Label>
                  <Select value={selectedOrder.delivery_status || 'Pending'} onValueChange={(v) => updateOrderStatus(selectedOrder.order_id, null, v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="In Transit">In Transit</SelectItem><SelectItem value="Delivered">Delivered</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>

              {/* Ordered Items */}
              <div>
                <h4 className="font-bold text-primary mb-3">Current Order Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted"><tr><th className="text-left p-3 text-sm font-medium">Item</th><th className="text-left p-3 text-sm font-medium">Size/Qty</th><th className="text-right p-3 text-sm font-medium">Qty</th><th className="text-right p-3 text-sm font-medium">Price</th><th className="text-right p-3 text-sm font-medium">Total</th></tr></thead>
                    <tbody className="divide-y">
                      {selectedOrder.ordered_items?.map((item, idx) => (
                        <tr key={idx}><td className="p-3 font-medium">{item.item_name}</td><td className="p-3 text-secondary">{item.size_or_quantity}</td><td className="p-3 text-right">{item.quantity}</td><td className="p-3 text-right">Rs.{item.price_per_unit?.toFixed(2)}</td><td className="p-3 text-right font-medium">Rs.{item.total_price?.toFixed(2)}</td></tr>
                      ))}
                      <tr className="bg-primary/5 font-bold"><td colSpan={4} className="p-3 text-right">Grand Total:</td><td className="p-3 text-right text-primary">Rs.{selectedOrder.total_amount?.toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedOrder.additional_requirements && (
                <div><h4 className="font-bold text-primary mb-2">Additional Requirements</h4><p className="text-secondary p-3 bg-amber-50 rounded-lg border border-amber-200">{selectedOrder.additional_requirements}</p></div>
              )}

              {/* Version Downloads */}
              <div>
                <h4 className="font-bold text-primary mb-3">PDF Downloads</h4>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(selectedOrder.order_id)} disabled={downloadingPdf}>
                    <Download className="h-4 w-4 mr-1" />Latest PDF
                  </Button>
                  {(selectedOrder.versions || []).map(v => (
                    <Button key={v.version} size="sm" variant="ghost" onClick={() => handleDownloadPdf(selectedOrder.order_id, v.version)} disabled={downloadingPdf}>
                      <FileText className="h-4 w-4 mr-1" />v{v.version} ({v.created_by === 'franchise' ? 'Original' : 'Altered'})
                    </Button>
                  ))}
                </div>
              </div>

              {/* Activity Timeline */}
              <div>
                <h4 className="font-bold text-primary mb-3 flex items-center gap-2"><History className="h-4 w-4" />Activity Log</h4>
                <OrderTimeline logs={selectedOrder.order_logs} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== REJECT MODAL ==================== */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Reject Order</DialogTitle>
            <DialogDescription>This will reject order {selectedOrder?.order_id}. Please provide a reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rejection Reason *</Label>
              <Textarea data-testid="reject-reason-input" placeholder="Enter reason for rejection..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} className="mt-1" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowRejectModal(false)}>Cancel</Button>
              <Button data-testid="confirm-reject-btn" variant="destructive" className="flex-1" disabled={!rejectReason.trim() || actionLoading === 'reject'} onClick={handleRejectSubmit}>
                {actionLoading === 'reject' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <X className="h-4 w-4 mr-2" />}Confirm Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== ALTER MODAL ==================== */}
      <Dialog open={showAlterModal} onOpenChange={setShowAlterModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2"><Edit3 className="h-5 w-5" />Alter Order - {selectedOrder?.order_id}</DialogTitle>
            <DialogDescription>Modify items, quantities, or add new items. Max 2 alterations per order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Items Editor */}
            <div className="space-y-3">
              {alterItems.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.item_name}</p>
                    <p className="text-xs text-muted-foreground">Rs.{(item.price_per_unit || 0).toFixed(2)} / {item.size_or_quantity}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => { const n = [...alterItems]; n[idx].quantity = Math.max(0, n[idx].quantity - 1); n[idx].total_price = n[idx].quantity * n[idx].price_per_unit; setAlterItems(n); }}>
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-10 text-center font-bold">{item.quantity}</span>
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => { const n = [...alterItems]; n[idx].quantity += 1; n[idx].total_price = n[idx].quantity * n[idx].price_per_unit; setAlterItems(n); }}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="text-sm font-bold min-w-[70px] text-right">Rs.{((item.quantity || 0) * (item.price_per_unit || 0)).toFixed(2)}</p>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => setAlterItems(prev => prev.filter((_, i) => i !== idx))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Add New Item */}
            {!showAddItemSelect ? (
              <Button variant="outline" className="w-full border-dashed" onClick={() => setShowAddItemSelect(true)}>
                <Plus className="h-4 w-4 mr-2" />Add New Item
              </Button>
            ) : (
              <div className="p-3 border rounded-lg space-y-2">
                <Label>Select Item to Add</Label>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {availableRawMaterials.filter(m => m.active !== false).map(m => (
                    <button key={m.material_id} className="w-full text-left p-2 text-sm rounded hover:bg-muted flex justify-between" onClick={() => addNewItemToAlter(m)}>
                      <span>{m.name}</span><span className="text-muted-foreground">Rs.{(m.price || m.default_price || 0).toFixed(2)}/{m.unit}</span>
                    </button>
                  ))}
                </div>
                <Button size="sm" variant="ghost" onClick={() => setShowAddItemSelect(false)}>Cancel</Button>
              </div>
            )}

            {/* Total */}
            <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg">
              <span className="font-semibold">New Total:</span>
              <span className="text-xl font-bold text-primary">Rs.{alterItems.reduce((s, i) => s + (i.quantity || 0) * (i.price_per_unit || 0), 0).toFixed(2)}</span>
            </div>

            <div>
              <Label>Additional Requirements</Label>
              <Textarea value={alterAdditionalReq} onChange={(e) => setAlterAdditionalReq(e.target.value)} rows={2} className="mt-1" />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowAlterModal(false)}>Cancel</Button>
              <Button data-testid="confirm-alter-btn" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" disabled={actionLoading === 'alter'} onClick={handleAlterSubmit}>
                {actionLoading === 'alter' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Edit3 className="h-4 w-4 mr-2" />}Save Alteration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RawMaterialOrders;
